/**
 * Turn plain product/page copy into simple block nodes.
 * Supports paragraphs (\n\n), single newlines as soft breaks inside a paragraph,
 * and bullet lines (- / • / *).
 */

export type ProseBlock =
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] };

const BULLET_RE = /^\s*[-•*]\s+/;

function isBulletLine(line: string): boolean {
  return BULLET_RE.test(line);
}

function stripBullet(line: string): string {
  return line.replace(BULLET_RE, "").trim();
}

export function parseProseBlocks(text: string): ProseBlock[] {
  const raw = text.replace(/\r\n/g, "\n").trim();
  if (!raw) return [];

  const chunks = raw.split(/\n\n+/).map((c) => c.trim()).filter(Boolean);
  const blocks: ProseBlock[] = [];

  for (const chunk of chunks) {
    const lines = chunk.split("\n").map((l) => l.trimEnd()).filter((l) => l.trim().length > 0);
    if (lines.length === 0) continue;

    if (lines.every(isBulletLine)) {
      blocks.push({ type: "ul", items: lines.map(stripBullet) });
      continue;
    }

    // Mixed: flush consecutive bullet runs as lists, other lines as paragraphs
    let paraLines: string[] = [];
    let bulletItems: string[] = [];

    const flushPara = () => {
      if (paraLines.length === 0) return;
      blocks.push({ type: "p", text: paraLines.join(" ").replace(/\s+/g, " ").trim() });
      paraLines = [];
    };
    const flushBullets = () => {
      if (bulletItems.length === 0) return;
      blocks.push({ type: "ul", items: bulletItems });
      bulletItems = [];
    };

    for (const line of lines) {
      if (isBulletLine(line)) {
        flushPara();
        bulletItems.push(stripBullet(line));
      } else {
        flushBullets();
        paraLines.push(line.trim());
      }
    }
    flushPara();
    flushBullets();
  }

  return blocks;
}
